import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  InputLabel,
  ListItemText,
  ListSubheader,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";

export interface GradeOption {
  id: number;
  grade: string;
}

interface Props {
  /** Active, backend-provided grades (id + grade). Never hardcoded. */
  grades: GradeOption[];
  /** Currently selected grade IDs. Empty array = "All Grades" (no restriction). */
  value: number[];
  onChange: (ids: number[]) => void;
  /** Accepts a responsive object so the control can fill a grid cell. */
  width?: number | string | { [k: string]: number | string };
}

/**
 * Searchable multi-select Grade filter — visually and behaviourally identical to
 * SubjectMultiSelect. Inclusive "Match Any" (and grade↔subject pair matching when
 * a subject is also selected) lives in the backend query; this control only
 * manages the selected grade-ID set.
 *
 * Collapsed display: "All Grades" (none) · single name · two short names ·
 * "Grades · N" (several). Menu stays open across selections, includes a search
 * box, Select All / Clear All, and a checkbox per grade.
 */
const GradeMultiSelect: React.FC<Props> = ({ grades, value, onChange, width = 160 }) => {
  const [search, setSearch] = useState("");

  const nameById = useMemo(() => {
    const m = new Map<number, string>();
    grades.forEach((g) => m.set(Number(g.id), g.grade));
    return m;
  }, [grades]);

  const filteredGrades = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q
      ? grades.filter((g) => String(g.grade || "").toLowerCase().includes(q))
      : grades;
  }, [grades, search]);

  const renderValue = (selected: number[]) => {
    if (!selected || selected.length === 0) return "All Grades";
    if (selected.length === 1) return nameById.get(Number(selected[0])) ?? "1 grade";
    const names = selected
      .map((id) => nameById.get(Number(id)))
      .filter(Boolean) as string[];
    if (selected.length === 2 && names.join(", ").length <= 20) return names.join(", ");
    return `Grades · ${selected.length}`;
  };

  const allIds = useMemo(() => grades.map((g) => Number(g.id)), [grades]);

  return (
    <FormControl size="small" sx={{ width }}>
      <InputLabel shrink>Grade</InputLabel>
      <Select
        multiple
        notched
        displayEmpty
        label="Grade"
        value={value}
        onChange={(e) => {
          const v = e.target.value;
          onChange(typeof v === "string" ? [] : (v as number[]).map(Number));
        }}
        renderValue={(selected) => renderValue(selected as number[])}
        sx={{ borderRadius: "10px" }}
        MenuProps={{
          autoFocus: false,
          PaperProps: { style: { maxHeight: 360, width: 280 } },
        }}
      >
        {/* Search + bulk actions — a non-selectable header so it never enters value */}
        <ListSubheader sx={{ bgcolor: "background.paper", px: 1, pt: 1, pb: 0.5 }}>
          <TextField
            size="small"
            autoFocus
            fullWidth
            placeholder="Search grades…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.stopPropagation()}
            InputProps={{ startAdornment: <SearchIcon sx={{ fontSize: 16, mr: 0.5, color: "text.disabled" }} /> }}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
          />
          <Box sx={{ display: "flex", justifyContent: "space-between", mt: 0.5 }}>
            <Button size="small" onClick={() => onChange(allIds)} sx={{ textTransform: "none", fontSize: 11.5, minWidth: 0 }}>
              Select All
            </Button>
            <Button size="small" onClick={() => onChange([])} sx={{ textTransform: "none", fontSize: 11.5, minWidth: 0 }} disabled={value.length === 0}>
              Clear All
            </Button>
          </Box>
        </ListSubheader>

        {filteredGrades.length === 0 && (
          <MenuItem disabled>
            <Typography sx={{ fontSize: 12.5, color: "text.secondary" }}>No grades found</Typography>
          </MenuItem>
        )}

        {filteredGrades.map((g) => {
          const id = Number(g.id);
          return (
            <MenuItem key={id} value={id} sx={{ py: 0.25 }}>
              <Checkbox size="small" checked={value.includes(id)} sx={{ py: 0.25, mr: 0.5 }} />
              <ListItemText primary={g.grade} primaryTypographyProps={{ fontSize: 13 }} />
            </MenuItem>
          );
        })}
      </Select>
    </FormControl>
  );
};

export default GradeMultiSelect;
